import React, {
  isValidElement,
  useCallback,
  useState,
  useMemo,
  useRef,
} from "react";
import type {
  MarketTemplateAllowableSelectionsType,
  MarketTemplateProps,
  MarketTemplateUpdateMarketType,
} from "../types";
import { MarketLayout } from "../components/MarketLayout/MarketLayout";
import { MarketTemplateSkeleton } from "../components/MarketTemplateSkeleton/MarketTemplateSkeleton";
import { Nav } from "components/src/components/Nav/Nav";
import { type PillNavItemObjType } from "components/src/components/Nav/types";
import { MarketShowType } from "../components/MarketLayout/types";
import { Card } from "components/src/components/Card/Card";
import { useCssFilter } from "shared/src/hooks/useCssFilter";
import { AlternateHeader } from "./components/AlternateHeader";
import { EventHeader } from "./components/EventHeader";
import isEmpty from "lodash-es/isEmpty";
import cx from "components/src/utils/cx";

import { handleShowMoreClick } from "../js/handleShowMoreClick";

const _defaultOnSelectionClick = () => {};
const defaultAllowableSelections = {
  value: Infinity,
  type: "total",
} as MarketTemplateAllowableSelectionsType;

export const SliderMarket = ({
  id,
  header,
  marketHeader,
  showMarketHeader = true,
  markets = [],
  marketFooter,
  footer = {},
  showBorder = false,
  showVersusDivider = false,
  type = "slider",
  versusText,
  groupBy = null,
  showLimit = 5,
  filters,
  canShowLess = true,
  oddsFormat = "american",
  initiallyOpen = true,
  hasSGP = false,
  hasFirebets,
  hasLivestream,
  isOddsBoost,
  sport,
  league,
  competitionType,
  skeleton,
  eventDetails,
  sliderKeyBy = "line",
  sliderKeyFrom = "first",
  allowableSelections = defaultAllowableSelections,
  animation = true,
  showMoreWagers,
  enableInternalSkeleton,
  showEventHeader,
  showTeamsInEventHeader,
  showEventHeaderChevron = true,
  swapEventHeaderCompetitors,
  eventHeaderCompetitionType,
  paddingClasses = "cui-pb-sm",
  topPadNoHeader = "cui-pt-sm",
  className = "",
  mode,
  mountHiddenMarkets,
  scrollType,
  updateMarket,
  onTemplateClick,
  onSelectionClick = _defaultOnSelectionClick,
  onHeaderClick,
  onEventHeaderClick,
  onFilterClick,
  onShowMoreClick,
  updateMarkets = () => {},
  ...rest
}: MarketTemplateProps & { filters: PillNavItemObjType[] }) => {
  const [ref, setRef] = useState(null);
  const [isOpen, setIsOpen] = useState(initiallyOpen);
  const [finishedRenderSetup, setFinishedRenderSetup] = useState({
    firstTime: true,
    value: false,
  });
  const { current: initialShowLimit } = useRef(showLimit);
  const [_showLimit, setShowLimit] = useState(showLimit);
  const [hasBeenToggled, setHasBeenToggled] = useState(false);
  // const filters = ["Slider View", "List View"];
  const [_activeFilter, setActiveFilter] = useState<string | number | null>(
    filters[0].label
  );

  const _onShowMoreClick = useCallback(() => {
    handleShowMoreClick({
      setShowLimit,
      _showLimit,
      showLimit,
      onShowMoreClick,
    });
  }, [_showLimit, filters, onShowMoreClick]);

  const onActiveItem = useCallback(
    (item) => {
      console.log({ item });
      const _active = filters.filter((f) => f.id === item).at(0) ?? filters[0];
      return setActiveFilter(_active.label);
    },
    [_activeFilter]
  );

  const _onTemplateClick = useCallback(
    (e) => {
      if (
        typeof onTemplateClick === "function" &&
        e.target?.offsetParent?.nodeName !== "BUTTON" &&
        e.target?.nodeName !== "BUTTON" &&
        e.target?.offsetParent?.id.includes("cui__card") &&
        !e.target?.classList.contains("rc-slider")
      ) {
        onTemplateClick(e);
      }
    },
    [onTemplateClick]
  );

  const _onToggle = useCallback((payload) => {
    const { isOpen, id } = payload;
    setIsOpen(isOpen);
    if (!hasBeenToggled) setHasBeenToggled(true);
    onHeaderClick && onHeaderClick(payload);
  }, []);


  const cardClasses = useMemo(()=>{
    return cx(
        {
          [paddingClasses]: isOpen && typeof paddingClasses === "string",
          [className]: typeof className === "string" && !!className,
          [topPadNoHeader as string]: typeof topPadNoHeader === "string" && (typeof header !== "object" || (!!header && !header?.hasHeader))
        },
        "cui__market-template"
      )
  },[paddingClasses, className, topPadNoHeader, header]);
  
  const showMoreTotal = useMemo(() => {
    if (typeof showLimit === "number") {
      return markets.length - showLimit;
    }
    return markets.length;
  }, [markets.length, showLimit]);

  const hasFilters = typeof filters !== "undefined" && !!filters.length;
  const hasEventDetails =
    typeof eventDetails !== "undefined" &&
    !isEmpty(eventDetails) &&
    (eventDetails?.eventName ||
      eventDetails?.isLive ||
      eventDetails?.startTime ||
      eventDetails?.home ||
      eventDetails?.away ||
      eventDetails?.competitor1 ||
      eventDetails?.competitor2);
  const _showEventHeader = hasEventDetails && showEventHeader;
  const hideOnFirstCollapsed = !initiallyOpen && !hasBeenToggled;
  const showCardSubheader =
    _showEventHeader &&
    (hasEventDetails || hasFilters) &&
    !hideOnFirstCollapsed;
  const _hasMarketHeader = showMarketHeader && marketHeader && !hideOnFirstCollapsed;

  const _hasSGP = hasSGP
    ? [{ leftIcon: { name: "ui_sgp", color: "fg-primary-strong", size: "lg" } }]
    : [];

  const _header = { ...header, headerDetails: _hasSGP };
  const hasFooterObj = footer && typeof footer === "object";

  useCssFilter(
    ref,
    _activeFilter,
    _showLimit,
    groupBy,
    hasFilters,
    hasBeenToggled,
    type,
    finishedRenderSetup,
    setFinishedRenderSetup
  );

  const _canShowMore =
    _showLimit !== "all" &&
    typeof _showLimit === "number" &&
    _showLimit < markets[0]?.selections?.length &&
    _activeFilter?.toString().toLowerCase() === "list view"
      ? true
      : false;
  const _canShowLess =
    canShowLess &&
    typeof _activeFilter === "string" &&
    _activeFilter?.toLowerCase() === "list view" &&
    initialShowLimit !== "all";

  // const hasHeader = !!header && typeof header === "object" && !isEmpty(header);
  // const _collapsible =
    // typeof header?.collapsible === "boolean" ? header?.collapsible : true;

  if (skeleton) return <MarketTemplateSkeleton animation={animation} type={type} />;

  return (
    <Card
      preventHeaderClickPropagation={true}
      {...rest}
      showBorder={showBorder}
      {...header?.computedHeader}
      collapsible={header?.collapsible}
      className={cardClasses}
      initiallyOpen={initiallyOpen}
      animation={animation}
      onToggle={_onToggle}
      onCardClick={_onTemplateClick}
      noPad
      ref={setRef as any}
    >
      {header && header?.isReactNode ? (<Card.Header>{header?.component}</Card.Header>) : null}

      {!hideOnFirstCollapsed ? (
        <Card.SubHeader>
          {_showEventHeader ? (
            <EventHeader
              id={id}
              isOddsBoost={isOddsBoost}
              onEventHeaderClick={onEventHeaderClick}
              eventDetails={eventDetails}
              sport={sport}
              showTeamsInEventHeader={showTeamsInEventHeader}
              mode={mode}
              showChevron={showEventHeaderChevron}
              swapCompetitors={swapEventHeaderCompetitors}
              competitionType={eventHeaderCompetitionType}
            />
          ) : null}
          <Nav
            className="cui-mb-xs"
            size="xs"
            items={filters}
            cssMode={true}
            showControls="none"
            overlay={true}
            onActiveItem={onActiveItem}
            skipLink={null}
            tabbable={false}
            animation={animation}
            variant="pill"
            simple
            forceMaxWidth
          />
        </Card.SubHeader>
      ) : null}

      {_hasMarketHeader ? (
        <MarketLayout.Header
          {...{ ...marketHeader, labels: null, noBorder: true }}
          className="cui-mb-1"
        />
      ) : null}
      {!hideOnFirstCollapsed && _activeFilter ? (
        <MarketLayout.Markets
          markets={markets}
          marketHeader={marketHeader}
          type={_activeFilter === "List View" ? "list-view-col" : "slider"}
          oddsFormat={oddsFormat}
          showVersusDivider={showVersusDivider}
          versusText={versusText}
          showLimit={_showLimit as MarketShowType}
          groupBy={groupBy as string}
          onSelectionClick={onSelectionClick}
          sliderKeyFrom={sliderKeyFrom}
          sliderKeyBy={sliderKeyBy}
          sport={sport}
          league={league}
          competitionType={competitionType}
          allowableSelections={allowableSelections}
          updateMarket={updateMarket as MarketTemplateUpdateMarketType}
        />
      ) : null}
      {isValidElement(footer) ? (
        <MarketLayout.Footer
          showLimit={_showLimit as MarketShowType}
          filters={filters}
          onShowMoreClick={_onShowMoreClick}
        >
          {footer}
        </MarketLayout.Footer>
      ) : (hasFooterObj && Object.keys(footer).length) ||
        (_showLimit && hasFooterObj) ? (
        <MarketLayout.Footer
          {...footer}
          showLimit={_showLimit as MarketShowType}
          filters={filters}
          showMore={_canShowMore}
          showMoreTotal={showMoreTotal}
          canShowLess={_canShowLess}
          className="cui-mt-1"
          onShowMoreClick={_onShowMoreClick}
        />
      ) : null}
    </Card>
  );
};
